import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useIsFocused } from "@react-navigation/native";
import { RouteProp } from "@react-navigation/core";

import { UserLoggedInNavigatorParamList } from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet";
import {
  ErrorFallback,
  LazyQueryNetworkBoundrary,
} from "../../../components/mobile/QueryNetworkBoundrary";
import { AHButton, AHEmptyScreen, Box, useLayoutContext } from "arkh";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  StorecastHeader,
  StorecastHeaderLoading,
} from "./StorecastHeader/StorecastHeader";
import { StorecastState } from "./types";
import { Storecast } from "./Storecast";
import {
  AppState,
  LayoutChangeEvent,
  Platform,
  useWindowDimensions,
} from "react-native";
import { useStorecastHeaderLayout } from "./StorecastHeader/useStorecastHeaderLayout";
import { useDebouncedCallback } from "use-debounce";
import { OrganisationHeaderHeight } from "../../../components/OrgHeaders/OrganisationHeader";
import { graphql, useLazyLoadQuery } from "react-relay";
import { PortalHost } from "@gorhom/portal";
import { useInterval } from "widgets/useInterval";
import { AbsolutePopoverHostName } from "../../../components/mobile/AbsolutePopover";
import { Lottie } from "../../../components/Lottie";
import { usePersistedDashboardId } from "../../../OS/Login/State";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";
import { StorecastScreenMobileQuery } from "./__generated__/StorecastScreenMobileQuery.graphql";

type Route = RouteProp<UserLoggedInNavigatorParamList, "Dashboard">;
type Navigation = NativeStackNavigationProp<
  UserLoggedInNavigatorParamList,
  "Dashboard"
>;

type StorecastScreenProps = {
  route: Route;
  navigation: Navigation;
  hasOrg: boolean;
  asHome: boolean;
  storeName: string;
};

const StorecastScreen_ = (props: StorecastScreenProps) => {
  return (
    <LazyQueryNetworkBoundrary
      newFetchKeyOnNavigation
      errorFallback={(p) => (
        <>
          <StorecastHeaderLoading
            onChangeDashboard={(dashboard) => {
              props.navigation.setParams({ dashboardId: dashboard?.id });
            }}
            onClose={() => {
              props.navigation.goBack();
            }}
            asHome={props.asHome}
            storeName={props.storeName}
            hasOrg={props.hasOrg}
            navigation={props.navigation}
          />
          <ErrorFallback {...p} />
        </>
      )}
      fallback={
        <StorecastHeaderLoading
          onChangeDashboard={(dashboard) => {
            props.navigation.setParams({ dashboardId: dashboard?.id });
          }}
          onClose={() => {
            props.navigation.goBack();
          }}
          asHome={props.asHome}
          storeName={props.storeName}
          hasOrg={props.hasOrg}
          navigation={props.navigation}
        />
      }
    >
      {(fetchKey: string) => (
        <InnerStorecastScreen {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
};
export const StorecastScreen = React.memo(StorecastScreen_, (prev, next) => {
  const equal =
    prev.asHome === next.asHome &&
    prev.hasOrg === next.hasOrg &&
    prev.storeName === next.storeName &&
    prev.route.params.dashboardId === next.route.params.dashboardId;
  return equal;
});

function InnerStorecastScreen(
  props: StorecastScreenProps & { fetchKey: string }
) {
  const { currentDashboardId } = usePersistedDashboardId();
  const { currentEmployeeId } = usePersistedEmployeeId();
  const [fk, setFetchKey] = useState(0);

  const [pollKey, setPollKey] = useState(0);

  const isFocused = useIsFocused();
  useInterval(() => {
    if (isFocused) {
      setPollKey((pk) => pk + 1);
    }
  }, 10000);

  useEffect(() => {
    let lastState = AppState.currentState;
    const listener = AppState.addEventListener("change", (state) => {
      if (state === "active" && lastState !== "active") {
        setFetchKey((fk) => fk + 1);
      }
      lastState = state;
    });
    return () => {
      listener.remove();
    };
  }, []);
  const data = useLazyLoadQuery<StorecastScreenMobileQuery>(
    graphql`
      query StorecastScreenMobileQuery($dashboardId: ID, $employeeId: ID)
      @throwOnFieldError {
        store {
          ...StorecastHeader_store
          ...Storecast_Store
        }

        dashboard(id: $dashboardId) {
          id
          ...Storecast_Dashboard
          ...StorecastHeader_dashboard
        }
      }
    `,
    {
      dashboardId: props.route.params.dashboardId ?? currentDashboardId,
      employeeId: currentEmployeeId,
    },
    {
      fetchKey: fk + ":" + props.fetchKey + ":" + pollKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [storecastState, _setStorecastState] = useState<StorecastState>("IDLE");
  const [_layout, setLayout] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const dimension = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const layout = useMemo(
    () =>
      Platform.OS === "android"
        ? { width: dimension.width, height: dimension.height }
        : _layout,
    [_layout, dimension.height, dimension.width]
  );
  const headerLayout = useStorecastHeaderLayout();
  const headerInset =
    headerLayout.height +
    insets.top +
    (props.asHome && props.hasOrg ? OrganisationHeaderHeight : 0);
  const containerHeight = useMemo(() => {
    if (!layout || layout.height < 1 || layout.width < 1) {
      return 0;
    }
    return layout.height - headerInset;
  }, [headerInset, layout]);
  const spaceMedium = useLayoutContext().space.medium;
  const unitSize = useMemo(() => {
    const verticalUnitSize =
      (containerHeight - Math.max(insets.bottom, spaceMedium)) / 4;
    if (Platform.OS === "web") {
      const horizontalUnitSize = (layout?.width ?? 0) / 8;
      return verticalUnitSize < horizontalUnitSize
        ? verticalUnitSize
        : horizontalUnitSize;
    }
    return verticalUnitSize;
  }, [containerHeight, insets.bottom, layout?.width, spaceMedium]);

  const _setLayout = useDebouncedCallback(setLayout, 100);
  const onLayout = useCallback(
    (e: LayoutChangeEvent) =>
      _setLayout({
        height: Math.floor(e.nativeEvent.layout.height),
        width: Math.floor(e.nativeEvent.layout.width),
      }),
    [_setLayout]
  );

  return (
    <BottomSheetModalProvider>
      <Box
        style={{ flex: 1 }}
        onLayout={Platform.OS === "android" ? undefined : onLayout}
      >
        <StorecastHeader
          state={storecastState}
          setStorecastState={_setStorecastState}
          navigation={props.navigation}
          dashboard={data.dashboard}
          store={data.store}
          asHome={props.asHome}
          hasOrg={props.hasOrg}
        />
        {data.dashboard ? (
          unitSize > 1 ? (
            <Storecast
              headerHeight={headerInset}
              dashboard={data.dashboard}
              store={data.store}
              key={data.dashboard.id}
              storecastState={storecastState}
              _setStorecastState={_setStorecastState}
              fetchKey={props.fetchKey}
              pollFetchKey={pollKey + ""}
              unitSize={unitSize}
            />
          ) : null
        ) : (
          <AHEmptyScreen
            title="This store doesn't have a dashboard yet"
            subtitle=""
            illustration={
              <Lottie
                autoPlay
                loop
                source={require("../../../assets/shake-a-empty-box.json")}
              />
            }
            cta={
              <AHButton
                onPress={() => {
                  props.navigation.navigateDeprecated("StorecastCreate", {});
                }}
                themeType="primary"
                title="Create a dashboard"
              />
            }
          />
        )}
        <PortalHost name={AbsolutePopoverHostName} />
      </Box>
    </BottomSheetModalProvider>
  );
}
